<template>
  <div class="basket-summary">
    <div class="basket-summary__item _title">
      {{ quantityText }} • {{ weightText }}
    </div>
    <div class="basket-summary__item">
      <div class="basket-summary__desc">
        Стоимость
      </div>
      <div class="basket-summary__val">
        <span class="nobr">{{ amountTotal }}</span>
      </div>
    </div>
    <div v-if="!isDeletedAll && discountAmountTotal" class="basket-summary__item">
      <div class="basket-summary__desc">
        Сумма скидки
      </div>
      <div class="basket-summary__val">
        <span class="nobr">-{{ discountAmountTotalFormatted }}</span>
      </div>
    </div>
    <div v-if="maxPayBonuses && !isDeletedAll" class="basket-summary__item _bonus-pay">
      <notification
          :is-show-icon="false"
          type="warning"
      >
        Вы также можете использовать
        <b class="nobr">{{ maxPayBonusesFormatted }}</b>
        с личного счёта на этапе оплаты
      </notification>
    </div>
    <div v-if="cashback && !isDeletedAll" class="basket-summary__item">
      <div class="basket-summary__desc">
        Кэшбэк за покупку
      </div>
      <div class="basket-summary__val">
        <span class="nobr">+{{ cashbackFormatted }}</span>
      </div>
    </div>
    <div class="basket-summary__item _total">
      <div class="basket-summary__desc">
        Итого без доставки
      </div>
      <div class="basket-summary__val">
        <span class="nobr">{{ amountDiscounted }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from '@vue/composition-api';
import { Notification } from '@book24/ui-components';

import { AMOUNT_DEFAULT_VALUE, DISCOUNT_AMOUNT_DEFAULT_VALUE } from '@/compositions/basket/basketStorage';

import plural from '@/utils/plural';
import { priceFormatted } from '@/utils/formatters/formattedPrice';
import { formattedGramsToKilograms } from '@/utils/formatters/formattedWeight';

export default defineComponent({
  name: 'BasketSummary',
  components: { Notification },
  props: {
    cashback: { type: Number, default: 0 },
    quantity: { type: Number, default: 0 },
    weight: { type: Number, default: 0 },
    maxPayBonuses: { type: [Number, String], default: 0 },
    amount: { type: Object, default: () => ({ ...AMOUNT_DEFAULT_VALUE }) },
    discountAmount: { type: Object, default: () => ({ ...DISCOUNT_AMOUNT_DEFAULT_VALUE }) },
    isDeletedAll: { type: Boolean, default: false },
  },
  setup(props) {
    const quantityText = computed(() => {
      const quantityPlural = plural(props.quantity, ['товар', 'товара', 'товаров']);
      return `${props.quantity} ${quantityPlural}`;
    });

    const weightText = computed(() => {
      const weightFormatted = formattedGramsToKilograms(props.weight);
      return `${weightFormatted} кг`;
    });

    const amountTotal = computed(() => priceFormatted(props.amount.total, { setFreeText: false }));

    const amountDiscounted = computed(() => priceFormatted(props.amount.discounted, { setFreeText: false }));

    const discountAmountTotal = computed(() => {
      const asNumber = Number(props.discountAmount.total);

      return Number.isNaN(asNumber) ? 0 : asNumber;
    });

    const discountAmountTotalFormatted = computed(() => priceFormatted(discountAmountTotal.value, { setFreeText: false }));

    const cashbackFormatted = computed(() => priceFormatted(props.cashback, { setFreeText: false }));

    const maxPayBonusesFormatted = computed(() => priceFormatted(props.maxPayBonuses, { setFreeText: false }));

    const bonusMnogoRu = computed(() => {
      const bonuses = Math.floor(props.amount.discounted / 50);
      if (props.amount.discounted <= 0 || bonuses <= 0) {
        return 0;
      }
      const bonusesPlural = plural(bonuses, ['бонус', 'бонуса', 'бонусов']);
      return `${bonuses} ${bonusesPlural}`;
    });

    return {
      quantityText,
      weightText,
      amountTotal,
      amountDiscounted,
      discountAmountTotal,
      discountAmountTotalFormatted,
      cashbackFormatted,
      maxPayBonusesFormatted,
      bonusMnogoRu,
    };
  },
});
</script>

<style lang="less">
.basket-summary {
  &__item {
    .p();

    display: flex;
    align-items: stretch;
    justify-content: space-between;
    margin-top: @size-x2;
    color: @black;

    &._title {
      margin-top: 0;
      color: @gray-dark;
    }

    &._total {
      padding-top: @size-x3;
      border-top: 1px solid @gray-super-light;
      align-items: baseline;
    }

    &._bonus-pay {
      display: block;
    }
  }

  &__val {
    flex: 0 1 100px;
    text-align: right;
  }

  &__item._total &__desc {
    .h3-mobile();
  }

  &__item._total &__val {
    .h2-mobile();
  }
}
</style>
